import { seedFromString } from '@/domain/core/rng';
import { scoreSession, type SessionScores } from '@/domain/scoring';
import type { CalibrationResult } from '@/state/store';
import type { DeviceProfile } from '@/platform';
import { getOrCreateParticipant } from './repositories/participants';
import { createSession, finishSession, getSession, getTrials, logEvent, toScoredTrials } from './repositories/sessions';
import { saveScores, SCORING_VERSION } from './repositories/scores';
import { SCHEMA_VERSION, type DeviceStamp, type ScoreRecord, type SessionArm, type SessionRecord } from './types';

export interface StartSessionInput {
  presentation?: SessionRecord["presentation"];
  programContext?: { id: string; day: number; version: string };
  participantCode: string;
  arm: SessionArm;
  supervision: 'clinician' | 'self';
  protocol: { id: string; version: string; raw: unknown };
  stimulusSet: { id: string; version: string; status: string };
  calibration: CalibrationResult | null;
  device: DeviceProfile | null;
  refresh: { hz: number; frameMs: number; sdDeltaMs: number };
  desynchronized: boolean;
  physicalLayout: boolean;
  trialCount: number;
}

export async function startSession(input: StartSessionInput): Promise<{ sessionId: string; participantId: string; seed: number }> {
  const participant = await getOrCreateParticipant(input.participantCode);
  const sessionId = crypto.randomUUID();
  // 시드는 참가자·arm·프로토콜 버전·세션 id 로 결정 — 같은 참가자가 같은 arm 을 다시 해도 순서가 다르고, 재현은 시드로 한다.
  const seed = seedFromString(`${participant.code}:${input.arm}:${input.protocol.id}@${input.protocol.version}:${sessionId}`);
  const protocolHash = await sha256(JSON.stringify(input.protocol.raw));

  const device: DeviceStamp = {
    platform: input.device?.platform ?? 'unknown',
    userAgent: input.device?.userAgent ?? navigator.userAgent,
    model: input.device?.model ?? null,
    osVersion: input.device?.osVersion ?? null,
    devicePixelRatio: input.device?.devicePixelRatio ?? window.devicePixelRatio,
    screenCssPx: input.device?.screenCssPx ?? { w: window.screen.width, h: window.screen.height },
    refreshHz: input.refresh.hz,
    frameMs: input.refresh.frameMs,
    frameSdMs: input.refresh.sdDeltaMs,
    timerResolutionMs: input.calibration?.timerResolutionMs ?? null,
    crossOriginIsolated: typeof crossOriginIsolated === 'boolean' ? crossOriginIsolated : false,
    desynchronized: input.desynchronized,
    inputModeHint: 'unknown',
    pxPerMm: input.calibration?.pxPerMm ?? null,
    viewingDistanceMm: input.calibration?.viewingDistanceMm ?? null,
    physicalLayout: input.physicalLayout,
  };

  const rec: SessionRecord = {
    ...(input.programContext ? { programContext: input.programContext } : {}),
    presentation: input.presentation ?? { theme: 'light', background: '#F7F9F8', foreground: '#263E3B', correctnessFeedback: 'practice-only', fontFamily: 'Pretendard' },
    id: sessionId, participantId: participant.id, participantCode: participant.code,
    arm: input.arm, supervision: input.supervision,
    protocolId: input.protocol.id, protocolVersion: input.protocol.version, protocolHash,
    stimulusSetId: input.stimulusSet.id, stimulusSetVersion: input.stimulusSet.version, stimulusSetStatus: input.stimulusSet.status,
    rngSeed: seed, appVersion: __APP_VERSION__, schemaVersion: SCHEMA_VERSION, signConvention: 'vigilance-positive',
    device, startedAt: new Date().toISOString(), endedAt: null, abortedReason: null, trialCount: input.trialCount, quality: null,
  };
  await createSession(rec);
  await logEvent({ sessionId, ts: performance.now(), at: rec.startedAt, topic: 'session-start', payload: { arm: input.arm, supervision: input.supervision } });
  return { sessionId, participantId: participant.id, seed };
}

/** 블록이 모두 끝난 뒤. 트라이얼을 DB 에서 다시 읽어 채점한다 — 메모리 결과가 아니라 저장된 것을 채점해야 재현 가능하다. */
export async function finalizeSession(sessionId: string): Promise<ScoreRecord> {
  const session = await getSession(sessionId);
  if (!session) throw new Error('세션을 찾을 수 없습니다');
  const trials = await getTrials(sessionId);
  const scores: SessionScores = scoreSession(toScoredTrials(trials));
  await finishSession(sessionId, { endedAt: new Date().toISOString() });
  const rec: ScoreRecord = {
    id: crypto.randomUUID(), sessionId, participantId: session.participantId, arm: session.arm,
    computedAt: new Date().toISOString(), scoringVersion: SCORING_VERSION, scores,
  };
  await saveScores(rec);
  await logEvent({ sessionId, ts: performance.now(), at: rec.computedAt, topic: 'scored', payload: { scoringVersion: SCORING_VERSION } });
  return rec;
}

async function sha256(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
